/**
 * Ob die Seitenleiste eingeklappt ist — als **Schiene** (HM-070).
 *
 * **Der Zustand steht nicht in der Adresse**, und das ist der Unterschied zu
 * allem, was seit HM-037 dorthin gewandert ist. Die Adresse sagt, *was zu sehen
 * ist*; ein eingeklapptes Menü ist keine andere Ansicht, sondern dieselbe mit
 * mehr Platz. Und sie wird geteilt: Wer einen Link auf einen Zähler verschickt,
 * würde dem Empfänger sonst seine eigene Vorliebe für schmale Leisten
 * mitschicken.
 *
 * **Er steht aber auch nicht nur im Speicher.** Ein `useState` wäre nach dem
 * nächsten Neuladen wieder weg — und ausgerechnet dieser Zustand wird einmal
 * gewählt und soll dann gelten. Er gehört damit in den Browser dessen, der ihn
 * gewählt hat, und in keine Datenbank: Er beschreibt kein Konto, sondern einen
 * Bildschirm.
 *
 * **Jeder Zugriff ist eingefasst.** `localStorage` wirft, wo Website-Daten
 * gesperrt sind — in einem privaten Fenster mancher Browser schon beim Lesen.
 * Ein Fehler dort darf die Anwendung nicht aufhalten; was dann gilt, ist die
 * ausgeklappte Leiste, denn die zeigt alles.
 */
import { useCallback, useEffect, useState } from "react";
import { trackUiAction } from "./usage.js";

const KEY = "hm.sidebar.collapsed";

/**
 * Was in der Ablage steht, gedeutet.
 *
 * **Nur `"1"` heißt eingeklappt.** Alles andere — nichts hinterlegt, ein Rest
 * aus einer früheren Fassung, von Hand hineingeschrieben — heißt ausgeklappt.
 * Ein `Boolean(wert)` machte aus jeder Zeichenkette ein „eingeklappt", auch aus
 * `"false"`.
 */
export function isCollapsedValue(value: string | null): boolean {
  return value === "1";
}

/** Die Gegenrichtung. Als Zeichenkette, weil `localStorage` nichts anderes kennt. */
export function collapsedValue(collapsed: boolean): string {
  return collapsed ? "1" : "0";
}

function read(): boolean {
  try {
    return isCollapsedValue(window.localStorage.getItem(KEY));
  } catch {
    return false;
  }
}

/**
 * Der Zustand samt Schalter.
 *
 * **Gelesen wird im Effekt und nicht beim ersten Zeichnen.** Der erste Aufbau
 * soll ohne Zugriff auf die Ablage auskommen — sonst hinge das erste Bild der
 * Anwendung an einem Aufruf, der in manchen Browsern wirft.
 */
export function useSidebarRail(): { collapsed: boolean; toggle: () => void } {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setCollapsed(read());
  }, []);

  const toggle = useCallback(() => {
    trackUiAction("sidebar.toggle");
    setCollapsed((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(KEY, collapsedValue(next));
      } catch {
        /* Gemerkt wird er dann nur für diese Sitzung. Das ist der richtige
           Ausgang: Die Leiste klappt trotzdem ein, sie erinnert sich nur beim
           nächsten Mal nicht. */
      }
      return next;
    });
  }, []);

  return { collapsed, toggle };
}
